import { useTranslation } from 'react-i18next';
import type { HierarchyRung } from '@/types/institution-templates';
import { humanizeUnitType, layoutHierarchy } from './hierarchy-layout';
import styles from './HierarchyDiagram.module.css';

/**
 * The template's structure, drawn as the containment it claims: connected rungs
 * top-down, each holding the one below, optional rungs dashed and tagged, auxiliary
 * unit types branching off the institution root.
 *
 * AN SVG RATHER THAN NESTED LISTS because the thing being explained is spatial —
 * "a Stream sits between Level and Class group, and you may skip it" is one glance
 * at a ladder and a paragraph of prose. All geometry comes from layoutHierarchy()
 * in hierarchy-layout.ts, a pure function with its own tests; this file only draws.
 *
 * THEME-AWARE BY CONSTRUCTION: every colour is a CSS variable or currentColor via
 * the module stylesheet. The SVG itself carries no hex anywhere.
 *
 * Accessible as a figure: role="img" with a summary label, plus a <title> on every
 * node so a screen reader walking the picture hears each rung in ladder order.
 */
export function HierarchyDiagram({
  hierarchy,
  auxiliaryUnitTypes,
  ariaLabel,
}: {
  hierarchy: readonly HierarchyRung[];
  auxiliaryUnitTypes: readonly string[];
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  const layout = layoutHierarchy(hierarchy, auxiliaryUnitTypes);

  if (layout.rungs.length === 0) {
    return <p className={styles.empty}>{t('templates.diagram.empty')}</p>;
  }

  return (
    <svg
      className={styles.diagram}
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      role="img"
      aria-label={ariaLabel}
    >
      <title>{ariaLabel}</title>

      {layout.edges.map((edge) => (
        <line
          key={`${edge.kind}-${edge.x1}-${edge.y1}-${edge.x2}-${edge.y2}`}
          className={edge.kind === 'ladder' ? styles.ladderEdge : styles.branchEdge}
          x1={edge.x1}
          y1={edge.y1}
          x2={edge.x2}
          y2={edge.y2}
          aria-hidden="true"
        />
      ))}

      {layout.rungs.map((rung) => {
        const unitName = t(`templates.unitTypes.${rung.unitType}`, {
          defaultValue: humanizeUnitType(rung.unitType),
        });
        return (
          <g key={`${rung.unitType}-${rung.depth}`} className={styles.rung}>
            <title>
              {rung.required
                ? t('templates.diagram.rungTitle', { depth: rung.depth, label: rung.label, unit: unitName })
                : t('templates.diagram.rungTitleOptional', {
                    depth: rung.depth,
                    label: rung.label,
                    unit: unitName,
                  })}
            </title>
            <rect
              className={rung.required ? styles.rungBox : `${styles.rungBox} ${styles.rungBoxOptional}`}
              x={rung.x}
              y={rung.y}
              width={rung.width}
              height={rung.height}
              rx={8}
            />
            <text className={styles.rungLabel} x={rung.x + 14} y={rung.y + 22}>
              {rung.label}
            </text>
            <text className={styles.rungUnit} x={rung.x + 14} y={rung.y + 40}>
              {unitName}
            </text>
            {!rung.required && (
              <text
                className={styles.optionalTag}
                x={rung.x + rung.width - 12}
                y={rung.y + 20}
                textAnchor="end"
              >
                {t('templates.diagram.optional')}
              </text>
            )}
          </g>
        );
      })}

      {layout.aux.length > 0 && (
        <text
          className={styles.auxHeading}
          x={layout.aux[0].x}
          y={layout.aux[0].y - 6}
        >
          {t('templates.diagram.auxiliary')}
        </text>
      )}

      {layout.aux.map((node) => {
        const unitName = t(`templates.unitTypes.${node.unitType}`, {
          defaultValue: humanizeUnitType(node.unitType),
        });
        return (
          <g key={node.unitType} className={styles.aux}>
            <title>{t('templates.diagram.auxTitle', { unit: unitName })}</title>
            <rect
              className={styles.auxBox}
              x={node.x}
              y={node.y}
              width={node.width}
              height={node.height}
              rx={node.height / 2}
            />
            <text
              className={styles.auxLabel}
              x={node.x + node.width / 2}
              y={node.y + node.height / 2 + 4}
              textAnchor="middle"
            >
              {unitName}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
